import { useState } from 'react'

import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'

import { RoleStatus } from '@b-stock/account-api-client'
import {
  useAccessToken,
  useAccessTokenRoles,
  useDecodedAccessToken,
  useDeliveryMethodModal,
  ShippingDestinationAddress,
} from '@b-stock/fe-core/bstock-next/components'
import {
  canReadAsBuyer,
  isZeroQuotePlaceholder,
} from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { myLocationsQuery } from '@b-stock/fe-core/bstock-next/queries'
import {
  Banner,
  Button,
  ButtonAsA,
  CurrencyStringCents,
  FormattedMessage,
  showSnackbar,
  SpinnerDots,
  Tag,
  TagType,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'
import type { LocationModel } from '@b-stock/location-api-client'
import { LocationAttribute } from '@b-stock/location-api-client'
import type { OrderResponseDto } from '@b-stock/order-api-client'
import type { LocationEligibilityDto } from '@b-stock/shipment-api-client'
import {
  LocationEligibilityDtoGeofencingEnum,
  ShipmentType,
} from '@b-stock/shipment-api-client'

import buyUrlPrefix from '@/buyer-portal/buyUrlPrefix'
import { useAddressWithinRadius } from '@/buyer-portal/components/features/listing/hooks/useAddressWithinRadius'
import useAutoQuoteGeneration from '@/buyer-portal/components/features/listing/hooks/useAutoQuoteGeneration'
import { useShippingModalTracking } from '@/buyer-portal/components/features/listing/hooks/useShippingModalTracking'
import { getZeroQuoteBannerMessageId } from '@/buyer-portal/components/features/listing/ListingActionColumn/getZeroQuoteBannerMessageId'
import { useSellerCollectsShipping } from '@/buyer-portal/components/features/listing/Shipment/useShipmentDetails'
import canChangeShipping from '@/buyer-portal/components/features/order/OrderDetailsLayout/canChangeShipping'
import {
  Card,
  CardHeader,
  CardTitle,
} from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import {
  getEditDeliveryMethodRedirectUri,
  isGeofencedFlatRateQuote,
} from '@/buyer-portal/helpers/shipping'
import useCompatibleRouter from '@/buyer-portal/helpers/useCompatibleRouter'
import { calculateShippingCostBreakdown } from '@/helpers/calculateShippingCostBreakdown'
import { useUpdateLocation } from '@/queries/addressMutations'

import {
  AddressContent,
  AddressDisclaimer,
  AddressRow,
  AddressText,
  Body,
  Divider,
  FlatRateLabel,
  FlatRateTag,
  HeaderInfoIcon,
  InfoIcon,
  InfoLabel,
  InfoRow,
  InfoValue,
  SellerCollectsTerms,
} from './ui'
import useChangeCarrier from './useChangeCarrier'

type ShippingInformationCardProps = {
  listing:
    | OrchestratedAuctionListingDetails
    | OrchestratedOfferingListingDetails
  selectedQuote?: OrchestratedQuote | null
  auctionOrOfferingEnded?: boolean
  buyerWon?: boolean
  order?: OrderResponseDto | null
  trackButtonClick: (buttonName: string) => void
}

export const ShippingInformationCard = ({
  auctionOrOfferingEnded,
  buyerWon,
  ...props
}: ShippingInformationCardProps) => {
  const accessToken = useAccessToken()
  const roles = useAccessTokenRoles()
  const isIntendedBuyer = roles?.buyer === RoleStatus.Intended

  const { isLoading: autoQuoteGenerationLoading, error: autoQuoteError } =
    useAutoQuoteGeneration(props.listing)

  if (!canReadAsBuyer(accessToken) && !isIntendedBuyer) return null
  if (auctionOrOfferingEnded && !buyerWon) return null

  return (
    <ShippingInformationCardContent
      {...props}
      auctionOrOfferingEnded={auctionOrOfferingEnded}
      autoQuoteGenerationLoading={autoQuoteGenerationLoading}
      hasAutoQuoteError={!!autoQuoteError}
    />
  )
}

const ShippingInformationCardContent = ({
  listing,
  selectedQuote,
  auctionOrOfferingEnded,
  order,
  trackButtonClick,
  autoQuoteGenerationLoading,
  hasAutoQuoteError,
}: ShippingInformationCardProps & {
  autoQuoteGenerationLoading: boolean
  hasAutoQuoteError: boolean
}) => {
  const [headerIconRef, setHeaderIconRef] = useState<HTMLElement | null>(null)
  const [estShippingIconRef, setEstShippingIconRef] =
    useState<HTMLElement | null>(null)
  const [flatRateIconRef, setFlatRateIconRef] = useState<HTMLElement | null>(
    null
  )
  const [singleQuoteIconRef, setSingleQuoteIconRef] =
    useState<HTMLElement | null>(null)

  const { formatMessage } = useIntl()
  const router = useCompatibleRouter()
  const { editDeliveryMethod, deliveryMethodChangePending } =
    useDeliveryMethodModal()
  const queryClient = useQueryClient()

  // `from` (pre-modal quote) within-radius; also warms its eligibility into cache.
  const fromWithinRadius = useAddressWithinRadius(listing._id, selectedQuote)

  // `to` within-radius read live at confirm from whatever the modal cached.
  const isGeofencedListing = !!listing.geofence
  const getToWithinRadius = (id?: string): boolean => {
    if (!isGeofencedListing || !id) return false
    const eligibility = queryClient
      .getQueriesData<LocationEligibilityDto[]>({
        queryKey: ['locationEligibility'],
      })
      .flatMap(([, data]) => data ?? [])
      // Scope to this listing: in-zone status is per-listing and the cache
      // persists eligibility across every listing visited this session.
      .find(
        (entry) => entry.locationId === id && entry.listingId === listing._id
      )
    return (
      eligibility?.geofencing === LocationEligibilityDtoGeofencingEnum.Inzone
    )
  }

  const trackingCallbacks = useShippingModalTracking(listing, selectedQuote, {
    fromWithinRadius,
    getToWithinRadius,
  })

  // Determine whether the buyer already has a default-deliverable address.
  // Mirrors useAutoQuoteGeneration so the auto-quote path and this promotion
  // share the same notion of "has a default" (same query key → shared cache).
  const accountId = useDecodedAccessToken()?.accountId
  const { data: defaultDeliverableData, isLoading: defaultDeliverableLoading } =
    myLocationsQuery.useWithOpts(
      [
        {
          attributes: [LocationAttribute.DefaultDeliverable],
          accountId: accountId ? [accountId] : [],
        },
      ],
      { enabled: !!accountId }
    )
  const hasDefault = (defaultDeliverableData?.locations.length ?? 0) > 0

  const { mutate: promoteToDefaultDeliverable } = useUpdateLocation({
    onSuccess: () => {
      showSnackbar({
        content: formatMessage({
          id: 'ShippingInformationCard.defaultDeliverableSet',
        }),
        options: { type: 'success', autoClose: true },
      })
    },
  })

  // Wrap the tracking onConfirm: fire telemetry first (unchanged), then — only
  // when the buyer has no default-deliverable yet — promote the address they
  // just chose so future listings auto-quote. The !hasDefault check is the
  // overwrite guard; an existing default is never replaced.
  const handleShippingConfirm = (data: {
    shipmentType: ShipmentType
    shippingCost?: number
    address?: LocationModel
    isEligible?: boolean
    shipTo?: string
  }) => {
    trackingCallbacks.onConfirm(data)

    const { address } = data
    // Skip promotion until the default-deliverable query has resolved: while it
    // is still loading `hasDefault` is a stale `false`, so promoting now could
    // silently overwrite an existing default the buyer already has.
    if (hasDefault || defaultDeliverableLoading || !address) return

    promoteToDefaultDeliverable(
      {
        id: address.id,
        originalId: address.originalId,
        partialLocationUpdateDto: {
          attributes: [
            ...(address.attributes ?? []).filter(
              (attr) =>
                attr !== LocationAttribute.Deliverable &&
                attr !== LocationAttribute.DefaultDeliverable
            ),
            LocationAttribute.Deliverable,
            LocationAttribute.DefaultDeliverable,
          ],
        },
      },
      {
        onError: () => {
          showSnackbar({
            content: formatMessage({
              id: 'ShippingInformationCard.defaultDeliverableSetError',
            }),
            options: { type: 'error', autoClose: true },
          })
        },
      }
    )
  }

  const shippingQuotePending =
    deliveryMethodChangePending || autoQuoteGenerationLoading

  const canEditDeliveryMethod =
    !auctionOrOfferingEnded || (order && canChangeShipping(order))

  const handleEdit = canEditDeliveryMethod
    ? () => {
        if (order) {
          if (!order.buyerAccountId) {
            throw new Error(
              `order.buyerAccountId was undefined for ${order._id} - this is unexpected`
            )
          }
          router.push(
            `${buyUrlPrefix}/user/${order.buyerAccountId}/orders/details/${order._id}?editShipping`
          )
        } else {
          editDeliveryMethod({
            ...trackingCallbacks,
            onConfirm: handleShippingConfirm,
            redirectURI: getEditDeliveryMethodRedirectUri(),
          })
          trackButtonClick(selectedQuote ? 'edit' : 'add_address')
        }
      }
    : undefined

  const isFlatRateQuote = selectedQuote?.shipmentType === ShipmentType.FlatRate
  const isGeofencedFlatRate = isGeofencedFlatRateQuote(selectedQuote)
  const isRegularFlatRateQuote = isFlatRateQuote && !isGeofencedFlatRate

  const carrierName = selectedQuote?.carrier?.name
  const transitDays = selectedQuote?.estimatedDeliveryDuration?.businessDays

  const {
    carrierModal,
    hasMultipleCarrierOptions,
    isCarrierChanging,
    openCarrierModal,
    shouldShowCarrierChange,
  } = useChangeCarrier({ hasAutoQuoteError, listing, order, selectedQuote })

  const sellerCollectsShipping = useSellerCollectsShipping(listing, order)

  const { estimatedShippingAmount } =
    calculateShippingCostBreakdown(selectedQuote)
  const isBuyerPickup = selectedQuote?.shipmentType === ShipmentType.BuyerPickup
  const isBuyerArranged =
    selectedQuote?.shipmentType === ShipmentType.BuyerArranged
  const isFreeShipping = selectedQuote?.shipmentType === ShipmentType.Free
  const isOtherShipping = selectedQuote?.shipmentType === ShipmentType.Other
  const isZeroQuoteValue = isZeroQuotePlaceholder(selectedQuote)
  const showEstShipping =
    !!selectedQuote &&
    !isBuyerPickup &&
    !isBuyerArranged &&
    !isFreeShipping &&
    !isOtherShipping &&
    selectedQuote.shipmentType !== ShipmentType.FlatRate &&
    estimatedShippingAmount > 0
  const showOtherShipping = isOtherShipping && estimatedShippingAmount > 0
  const isFixedShipmentType =
    isBuyerPickup ||
    isBuyerArranged ||
    isFreeShipping ||
    isOtherShipping ||
    isRegularFlatRateQuote
  const showShippingRow =
    isBuyerPickup ||
    isBuyerArranged ||
    isFreeShipping ||
    showOtherShipping ||
    showEstShipping ||
    isRegularFlatRateQuote

  // On a Seller Collects listing B-Stock has a rate only when it booked the
  // freight; every other mode would otherwise fall through to $0.00 or a blank.
  const showSellerCollectsRow =
    sellerCollectsShipping.isSellerCollects &&
    (!sellerCollectsShipping.showQuotedAmount || estimatedShippingAmount > 0)

  // The buyer collects, so there is no delivery address B-Stock holds or can
  // change, no quoted rate, and nothing to label "Set by you". The design
  // replaces all of it with where to collect from and how to get there.
  // Every Seller Collects mode but BOOKED_THROUGH_BSTOCK: B-Stock holds no
  // rate for these, so an absent figure is correct rather than a failure.
  const isSellerCollectsUnquoted =
    sellerCollectsShipping.isSellerCollects &&
    !sellerCollectsShipping.showQuotedAmount

  const pickup =
    sellerCollectsShipping.isSellerCollects &&
    sellerCollectsShipping.pickupLineId !== undefined &&
    sellerCollectsShipping.coordinationId !== undefined &&
    sellerCollectsShipping.origin !== undefined
      ? {
          lineId: sellerCollectsShipping.pickupLineId,
          coordinationId: sellerCollectsShipping.coordinationId,
          origin: sellerCollectsShipping.origin,
          href: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
            sellerCollectsShipping.origin
          )}`,
        }
      : undefined

  return (
    <Card data-testid="shipping-information-widget">
      <CardHeader $variant="sidebar">
        <CardTitle $variant="sidebar">
          <FormattedMessage id="ShippingInformationCard.title" />
          <HeaderInfoIcon ref={setHeaderIconRef}>
            <FontAwesomeIcon icon={faInfoCircle} size="sm" aria-hidden />
          </HeaderInfoIcon>
          {headerIconRef && (
            <Tooltip referenceElement={headerIconRef} placement="top">
              <FormattedMessage id="ShippingInformationCard.headerTooltip" />
            </Tooltip>
          )}
        </CardTitle>
      </CardHeader>

      <Body $variant="sidebar">
        {/* A zero quote is the right answer wherever B-Stock does not book the
            freight — only Whirlpool is BOOKED_THROUGH_BSTOCK — so framing it as
            a failure and asking the buyer to refresh describes a correct state
            as an error. Where B-Stock does quote, a zero still is one. */}
        {isZeroQuoteValue && !isSellerCollectsUnquoted && (
          <Banner type="warning">
            <FormattedMessage id={getZeroQuoteBannerMessageId(selectedQuote)} />
          </Banner>
        )}

        <AddressRow>
          <AddressContent>
            <AddressText>
              {pickup ? (
                <FormattedMessage
                  id={pickup.lineId}
                  values={{ origin: pickup.origin }}
                />
              ) : shippingQuotePending ? (
                <SpinnerDots />
              ) : selectedQuote ? (
                <ShippingDestinationAddress quote={selectedQuote} />
              ) : (
                <FormattedMessage id="ShippingInformationCard.noAddress" />
              )}
            </AddressText>
            {pickup ? (
              <AddressDisclaimer data-testid="seller-collects-pickup-coordination">
                <FormattedMessage
                  id={pickup.coordinationId}
                  values={{ sellerName: sellerCollectsShipping.sellerName }}
                />
              </AddressDisclaimer>
            ) : (
              selectedQuote && (
                <AddressDisclaimer>
                  <FormattedMessage id="ShippingInformationCard.addressDisclaimer" />
                </AddressDisclaimer>
              )
            )}
          </AddressContent>
          {pickup ? (
            <ButtonAsA
              href={pickup.href}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              appearance="secondary"
              data-testid="seller-collects-directions"
            >
              <FormattedMessage id="ShippingInformationCard.directions" />
            </ButtonAsA>
          ) : (
            handleEdit &&
            (shippingQuotePending ? null : (
              <Button
                type="button"
                size="small"
                appearance="secondary"
                onClick={handleEdit}
              >
                <FormattedMessage
                  id={
                    selectedQuote
                      ? 'Auction.details.receiveGoods.edit'
                      : 'Common.set'
                  }
                />
              </Button>
            ))
          )}
        </AddressRow>

        {isGeofencedFlatRate && selectedQuote && (
          <FlatRateTag>
            <FlatRateLabel>
              <FormattedMessage id="ShippingInformationCard.geofencedFlatRate" />
            </FlatRateLabel>
            <CurrencyStringCents
              value={selectedQuote.totalPrice}
              currency={listing.currency ?? 'USD'}
            />
            <InfoIcon ref={setFlatRateIconRef}>
              <FontAwesomeIcon icon={faInfoCircle} size="sm" aria-hidden />
            </InfoIcon>
            {flatRateIconRef && (
              <Tooltip referenceElement={flatRateIconRef} placement="top">
                <FormattedMessage id="ShippingInformationCard.geofencedFlatRateTooltip" />
              </Tooltip>
            )}
          </FlatRateTag>
        )}

        {sellerCollectsShipping.isSellerCollects && !pickup && (
          <>
            <Divider />
            <SellerCollectsTerms data-testid="seller-collects-shipping-terms">
              <FormattedMessage
                id={sellerCollectsShipping.descriptionId}
                values={{
                  sellerName: sellerCollectsShipping.sellerName,
                  ...(sellerCollectsShipping.origin
                    ? { origin: sellerCollectsShipping.origin }
                    : {}),
                }}
              />
            </SellerCollectsTerms>
            {showSellerCollectsRow && (
              <InfoRow data-testid="seller-collects-shipping-rate">
                <InfoLabel>
                  <FormattedMessage
                    id={sellerCollectsShipping.rateLabelId}
                    values={{
                      hasMode: selectedQuote?.transportMode ? 'yes' : 'no',
                      mode: selectedQuote?.transportMode ?? '',
                    }}
                  />
                </InfoLabel>
                <InfoValue>
                  {sellerCollectsShipping.showQuotedAmount ? (
                    <CurrencyStringCents
                      value={estimatedShippingAmount}
                      currency={listing.currency ?? 'USD'}
                    />
                  ) : (
                    <FormattedMessage
                      id={sellerCollectsShipping.rateValueId}
                      values={{ sellerName: sellerCollectsShipping.sellerName }}
                    />
                  )}
                </InfoValue>
              </InfoRow>
            )}
          </>
        )}

        {/* Carrier and ship window — only shown when a quote is selected */}
        {selectedQuote && (
          <>
            {showShippingRow && !sellerCollectsShipping.isSellerCollects && (
              <>
                <Divider />
                <InfoRow>
                  <InfoLabel>
                    <FormattedMessage
                      id={
                        isFixedShipmentType
                          ? 'ShippingInformationCard.shipping'
                          : 'ShippingInformationCard.estShipping'
                      }
                    />
                    {isFixedShipmentType ? (
                      <Tag type={TagType.info}>
                        <FormattedMessage
                          id={
                            isBuyerPickup
                              ? 'TotalPaymentInfo.shippingTags.BUYER_PICKUP'
                              : isBuyerArranged
                                ? 'TotalPaymentInfo.shippingTags.BUYER_ARRANGED'
                                : isFreeShipping
                                  ? 'TotalPaymentInfo.shippingTags.FREE'
                                  : isRegularFlatRateQuote
                                    ? 'TotalPaymentInfo.shippingTags.FLAT_RATE'
                                    : 'TotalPaymentInfo.shippingTags.OTHER'
                          }
                        />
                      </Tag>
                    ) : (
                      selectedQuote.transportMode && (
                        <span>({selectedQuote.transportMode})</span>
                      )
                    )}
                    {!isFixedShipmentType && (
                      <>
                        <InfoIcon ref={setEstShippingIconRef}>
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            size="sm"
                            aria-hidden
                          />
                        </InfoIcon>
                        {estShippingIconRef && (
                          <Tooltip
                            referenceElement={estShippingIconRef}
                            placement="top"
                          >
                            <FormattedMessage id="ShippingInformationCard.estShippingTooltip" />
                          </Tooltip>
                        )}
                      </>
                    )}
                  </InfoLabel>
                  <InfoValue>
                    <CurrencyStringCents
                      value={
                        isBuyerPickup || isBuyerArranged || isFreeShipping
                          ? 0
                          : estimatedShippingAmount
                      }
                      currency={listing.currency ?? 'USD'}
                    />
                  </InfoValue>
                </InfoRow>
              </>
            )}
            {carrierName && (
              <>
                <Divider />
                <InfoRow>
                  <InfoLabel>
                    <FormattedMessage id="ShippingInformationCard.carrier" />
                    {shouldShowCarrierChange &&
                      (hasMultipleCarrierOptions ? (
                        isCarrierChanging ? (
                          <SpinnerDots />
                        ) : (
                          <Button
                            type="button"
                            size="small"
                            appearance="plainTertiary"
                            onClick={openCarrierModal}
                          >
                            <FormattedMessage id="Common.changeCarrier" />
                          </Button>
                        )
                      ) : (
                        <span data-testid="single-quote-info-icon">
                          <InfoIcon ref={setSingleQuoteIconRef}>
                            <FontAwesomeIcon
                              icon={faInfoCircle}
                              size="sm"
                              aria-hidden
                            />
                          </InfoIcon>
                          {singleQuoteIconRef && (
                            <Tooltip
                              referenceElement={singleQuoteIconRef}
                              placement="top"
                            >
                              <FormattedMessage id="ChangeCarrierModal.singleQuoteInfo" />
                            </Tooltip>
                          )}
                        </span>
                      ))}
                  </InfoLabel>
                  <InfoValue>{carrierName}</InfoValue>
                </InfoRow>
              </>
            )}
            {transitDays !== undefined && (
              <>
                <Divider />
                <InfoRow>
                  <InfoLabel>
                    <FormattedMessage id="ShippingInformationCard.shipWindow" />
                  </InfoLabel>
                  <InfoValue>
                    <FormattedMessage
                      id="ShippingInformationCard.businessDays"
                      values={{ days: transitDays }}
                    />
                  </InfoValue>
                </InfoRow>
              </>
            )}
          </>
        )}
      </Body>
      {carrierModal}
    </Card>
  )
}
